/* iter55g35 — Inline suggestion overlays for the editable manuscript.
 *
 * The editable path in HighlightedParagraph is an "uncontrolled" DOM
 * node — React must never touch its innerHTML/innerText after mount, or
 * cut/paste/undo/Grammarly break (see the HighlightedParagraph header).
 *
 * This hook keeps the anchored-suggestion visibility WITHOUT touching
 * the editable node's contents. Instead:
 *
 * 1. It walks the DOM under `containerRef` to locate each occurrence of
 *    every anchor_text in the pending suggestions list.
 * 2. It builds a native `Range` around each occurrence, calls
 *    `getClientRects()` to get pixel rectangles (one rect per line the
 *    range spans), and converts them into container-relative coords so
 *    an absolute-positioned overlay layer above the prose can render a
 *    coloured underline directly on top of the words.
 * 3. It re-computes on: suggestion-list change, ResizeObserver on the
 *    container (typography reflow), MutationObserver on the container
 *    (writer typing / paste / delete), and window resize.
 *
 * The overlay layer itself lives in ManuscriptBody and is rendered ONLY
 * when `paragraphsEditable === true`, because the read-only path
 * already renders inline `<mark>` highlights via HighlightedParagraph.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

function collectTextSegments(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const segs = [];
  let acc = 0;
  let node;
  // eslint-disable-next-line no-cond-assign
  while ((node = walker.nextNode())) {
    const len = node.data.length;
    segs.push({ node, start: acc, end: acc + len });
    acc += len;
  }
  return { segs, flat: segs.map((s) => s.node.data).join('') };
}

function computeOverlaysForContainer(container, suggestions) {
  if (!container || !suggestions?.length) return [];
  const cRect = container.getBoundingClientRect();
  const scrollTop = container.scrollTop;
  const scrollLeft = container.scrollLeft;
  const paras = container.querySelectorAll('[data-testid^="manuscript-para-"]');
  const out = [];
  for (const p of paras) {
    // Skip if paragraph has no text at all. Use textContent (widely
    // supported in jsdom + browsers) instead of innerText.
    if (!p.textContent) continue;
    const { segs, flat } = collectTextSegments(p);
    if (!flat) continue;
    for (const s of suggestions) {
      const anchor = s?.anchor_text;
      if (!anchor || anchor.length < 1) continue;
      let idx = flat.indexOf(anchor);
      while (idx !== -1) {
        const startCharIdx = idx;
        const endCharIdx = idx + anchor.length;
        const startSeg = segs.find(
          (seg) => startCharIdx >= seg.start && startCharIdx < seg.end,
        );
        const endSeg = segs.find(
          (seg) => endCharIdx > seg.start && endCharIdx <= seg.end,
        );
        if (startSeg && endSeg) {
          try {
            const range = document.createRange();
            range.setStart(startSeg.node, startCharIdx - startSeg.start);
            range.setEnd(endSeg.node, endCharIdx - endSeg.start);
            const rects = Array.from(range.getClientRects())
              .filter((r) => r.width > 0 && r.height > 0)
              .map((r) => ({
                top:    r.top - cRect.top + scrollTop,
                left:   r.left - cRect.left + scrollLeft,
                width:  r.width,
                height: r.height,
              }));
            if (rects.length) {
              out.push({
                id:         `${s.id}-${p.dataset.testid}-${startCharIdx}`,
                suggestion: s,
                rects,
              });
            }
          } catch (_e) { /* ignore range/DOM race */ }
        }
        idx = flat.indexOf(anchor, endCharIdx);
      }
    }
  }
  return out;
}

/**
 * @param {React.RefObject<HTMLElement>} containerRef  Prose container.
 * @param {Array}   suggestions   Pending suggestions with anchor_text.
 * @param {boolean} enabled       Master toggle (usually paragraphsEditable).
 */
export default function useSuggestionOverlays(containerRef, suggestions, enabled) {
  const [overlays, setOverlays] = useState([]);
  const debounceRef = useRef(null);

  const compute = useCallback(() => {
    if (!enabled) { setOverlays([]); return; }
    setOverlays(computeOverlaysForContainer(containerRef.current, suggestions));
  }, [containerRef, suggestions, enabled]);

  const scheduleCompute = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(compute, 120);
  }, [compute]);

  // Recompute on prop changes.
  useLayoutEffect(() => { compute(); }, [compute]);

  // Recompute on DOM edits / resize.
  useEffect(() => {
    if (!enabled) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const ro = new ResizeObserver(scheduleCompute);
    ro.observe(container);

    const mo = new MutationObserver(scheduleCompute);
    mo.observe(container, {
      subtree: true, characterData: true, childList: true,
    });

    window.addEventListener('resize', scheduleCompute);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', scheduleCompute);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enabled, containerRef, scheduleCompute]);

  return overlays;
}
